import { ChangeDetectionStrategy, Component, inject, signal } from '@angular/core';
import { FormBuilder, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { MessageService } from 'primeng/api';
import { Button } from 'primeng/button';
import { Checkbox } from 'primeng/checkbox';
import { Dialog } from 'primeng/dialog';
import { InputTextModule } from 'primeng/inputtext';
import { Select } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { Textarea } from 'primeng/textarea';
import { HttpErrorResponse } from '@angular/common/http';
import { finalize } from 'rxjs/operators';
import type {
  ChatSuggestionAdminRow,
  UpdateChatSuggestionRequest,
} from '../../models/chat-suggestions-admin.model';
import {
  CHAT_SUGGESTION_AGENT_IDS,
  type ChatSuggestionAgentId,
} from '../../config/chat-suggestion-agents.config';
import { ChatSuggestionsAdminService } from '../../services/chat-suggestions-admin.service';

/** Agent filter value: empty string means all agents. */
type AgentFilterValue = '' | ChatSuggestionAgentId;

/**
 * Operator UI for CRUD on chat suggestion chips (fa-app-service `/v1/prototype/chat/suggestions` admin API).
 * Routed from the BrightNav admin subapp; clears consumer chip cache after successful writes via
 * {@link ChatSuggestionsAdminService}. The grid uses PrimeNG `p-table` with client-side sort on
 * **Active** and **Modified** (default: newest `modifiedAt` first), and paginator (fixed page size,
 * hidden when all rows fit on one page). Long lists scroll with the BrightNav admin shell.
 */
@Component({
  selector: 'lib-chat-suggestions-admin',
  standalone: true,
  imports: [
    FormsModule,
    ReactiveFormsModule,
    Button,
    Checkbox,
    Dialog,
    InputTextModule,
    Select,
    TableModule,
    Textarea,
  ],
  templateUrl: './chat-suggestions-admin.component.html',
  styleUrl: './chat-suggestions-admin.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ChatSuggestionsAdminComponent {
  private readonly _admin = inject(ChatSuggestionsAdminService);
  private readonly _fb = inject(FormBuilder);
  private readonly _messages = inject(MessageService);

  readonly agentIds = CHAT_SUGGESTION_AGENT_IDS;

  readonly agentFilterOptions: { label: string; value: AgentFilterValue }[] = [
    { label: 'All agents', value: '' },
    ...CHAT_SUGGESTION_AGENT_IDS.map((id) => ({ label: id, value: id as AgentFilterValue })),
  ];

  readonly agentCreateOptions = CHAT_SUGGESTION_AGENT_IDS.map((id) => ({
    label: id,
    value: id,
  }));

  readonly rows = signal<ChatSuggestionAdminRow[]>([]);
  readonly agentFilter = signal<AgentFilterValue>('');

  /** Rows per page for the admin table (fixed; no rows-per-page dropdown on the paginator). */
  readonly tableRowsPerPage = 10;

  readonly loading = signal(false);
  readonly dialogOpen = signal(false);
  readonly saving = signal(false);
  readonly isEditMode = signal(false);
  /** Row being edited; drives whether `version` is re-sent when the field is left blank. */
  private readonly _editingRow = signal<ChatSuggestionAdminRow | null>(null);

  readonly form = this._fb.nonNullable.group({
    agentId: ['', [Validators.required]],
    cta: ['', [Validators.required]],
    macro: ['', [Validators.required]],
    active: [true],
    version: [''],
    clearVersion: [false],
  });

  constructor() {
    this.reload();
  }

  onAgentFilterChange(value: AgentFilterValue): void {
    this.agentFilter.set(value);
    this.reload();
  }

  openCreate(): void {
    this._editingRow.set(null);
    this.isEditMode.set(false);
    this.form.reset({
      agentId: BRIGHTNAV_DEFAULT_AGENT,
      cta: '',
      macro: '',
      active: true,
      version: '',
      clearVersion: false,
    });
    this.form.controls['agentId'].enable();
    this.dialogOpen.set(true);
  }

  openEdit(row: ChatSuggestionAdminRow): void {
    this._editingRow.set(row);
    this.isEditMode.set(true);
    this.form.patchValue({
      agentId: row.agentId ?? '',
      cta: row.cta,
      macro: row.macro,
      active: row.active,
      version: row.version !== undefined && row.version !== null ? String(row.version) : '',
      clearVersion: false,
    });
    this.form.controls['agentId'].enable();
    this.dialogOpen.set(true);
  }

  closeDialog(): void {
    this.dialogOpen.set(false);
    this._editingRow.set(null);
  }

  save(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }
    const raw = this.form.getRawValue();
    this.saving.set(true);
    if (this.isEditMode()) {
      const key = this._editingRow()?.key;
      if (!key) {
        this.saving.set(false);
        return;
      }
      const orig = this._editingRow();
      const updateBody = this.buildUpdatePayload(raw, orig);
      this._admin
        .update(key, updateBody)
        .pipe(finalize(() => this.saving.set(false)))
        .subscribe({
          next: () => {
            this._messages.add({ severity: 'success', summary: 'Saved', life: 2500 });
            this.closeDialog();
            this.reload();
          },
          error: (err) => this.handleHttpError(err, 'Save failed'),
        });
    } else {
      const versionTrim = raw.version.trim();
      const versionNum = Number(versionTrim);
      this._admin
        .create({
          agentId: raw.agentId,
          cta: raw.cta,
          macro: raw.macro,
          active: raw.active,
          version:
            versionTrim === ''
              ? undefined
              : Number.isNaN(versionNum)
                ? versionTrim
                : versionNum,
        })
        .pipe(finalize(() => this.saving.set(false)))
        .subscribe({
          next: () => {
            this._messages.add({ severity: 'success', summary: 'Created', life: 2500 });
            this.closeDialog();
            this.reload();
          },
          error: (err) => this.handleHttpError(err, 'Create failed'),
        });
    }
  }

  private buildUpdatePayload(
    raw: {
      agentId: string;
      cta: string;
      macro: string;
      active: boolean;
      version: string;
      clearVersion: boolean;
    },
    orig: ChatSuggestionAdminRow | null
  ): UpdateChatSuggestionRequest {
    const base: UpdateChatSuggestionRequest = {
      agentId: raw.agentId.trim(),
      cta: raw.cta,
      macro: raw.macro,
      active: raw.active,
      clearVersion: raw.clearVersion,
    };
    if (raw.clearVersion) {
      return base;
    }
    const trimmed = raw.version.trim();
    if (trimmed !== '') {
      const n = Number(trimmed);
      return { ...base, version: Number.isNaN(n) ? trimmed : n };
    }
    if (orig?.version !== undefined && orig.version !== null) {
      return { ...base, version: orig.version };
    }
    return base;
  }

  deleteRow(row: ChatSuggestionAdminRow): void {
    if (!window.confirm(`Delete suggestion "${row.cta}"? This cannot be undone.`)) {
      return;
    }
    this.loading.set(true);
    this._admin
      .delete(row.key)
      .pipe(finalize(() => this.loading.set(false)))
      .subscribe({
        next: () => {
          this._messages.add({ severity: 'success', summary: 'Deleted', life: 2500 });
          this.reload();
        },
        error: (err) => this.handleHttpError(err, 'Delete failed'),
      });
  }

  reload(): void {
    this.loading.set(true);
    const filter = this.agentFilter();
    this._admin
      .listIncludeInactive(filter || undefined)
      .pipe(finalize(() => this.loading.set(false)))
      .subscribe({
        next: (list) => this.rows.set(list),
        error: (err) => this.handleHttpError(err, 'Could not load suggestions'),
      });
  }

  macroPreview(macro: string): string {
    const max = 80;
    return macro.length <= max ? macro : `${macro.slice(0, max)}…`;
  }

  private handleHttpError(err: unknown, summary: string): void {
    const status = err instanceof HttpErrorResponse ? err.status : 0;
    const httpErr = err instanceof HttpErrorResponse ? err : null;
    const detail =
      httpErr && httpErr.error !== undefined && httpErr.error !== null
        ? typeof httpErr.error === 'object'
          ? JSON.stringify(httpErr.error)
          : String(httpErr.error)
        : httpErr
          ? httpErr.message
          : String(err);
    this._messages.add({
      severity: 'error',
      summary,
      detail: status ? `${detail} (HTTP ${status})` : detail,
      life: 6000,
    });
  }
}

const BRIGHTNAV_DEFAULT_AGENT: ChatSuggestionAgentId = 'brightnav';
